import { BarChart3, Loader2, Layers, Tag as TagIcon } from 'lucide-react';
import { SlimSidebar } from '../layout/SlimSidebar';
import { useTagKpi, type TagKpiTag } from '../../hooks/useTagKpi';
import { HelpHint } from '../ui/HelpHint';

/**
 * Vista KPI Tag: panoramica aggregata (non centinaia di tag).
 * Mostra le CATEGORIE (poche) con conteggio e i tag principali (top ~50)
 * con conteggio e pallino colore. Barre in puro Tailwind, nessuna libreria grafica.
 */
export function TagKpiView() {
  const { data, isLoading, isError } = useTagKpi();

  const categories = data?.categories ?? [];
  const tags = (data?.tags ?? []).slice(0, 50);
  const total = data?.total_messages ?? 0;

  // Valori massimi per scalare le barre
  const maxCat = Math.max(1, ...categories.map((c) => c.count));
  const maxTag = Math.max(1, ...tags.map((t) => t.count));

  return (
    <div className="flex flex-1 overflow-hidden">
      <SlimSidebar />

      <section className="flex flex-1 flex-col bg-zinc-950">
        {/* Header */}
        <div className="flex h-10 items-center gap-2 border-b border-zinc-800/80 px-4 text-xs">
          <BarChart3 size={12} className="text-accent-400" />
          <span className="font-medium text-zinc-200">KPI Tag</span>
          {/* Aiuto contestuale della sezione */}
          <HelpHint topic="kpi" />
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500">
            {total.toLocaleString('it-IT')} messaggi totali
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={16} className="animate-spin text-zinc-600" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              Impossibile caricare i KPI dei tag. Riprova più tardi.
            </div>
          ) : categories.length === 0 && tags.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              Nessun dato disponibile: i tag vengono creati automaticamente dalle regole AI.
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-8">
              {/* Categorie */}
              {categories.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    <Layers size={11} /> Categorie
                  </div>
                  <ul className="space-y-2">
                    {categories.map((c) => (
                      <li key={c.category} className="flex items-center gap-3">
                        <span className="w-40 shrink-0 truncate text-xs text-zinc-300">
                          {c.category || '(senza categoria)'}
                        </span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-800/80">
                          <div
                            className="h-full rounded-full bg-accent-500"
                            style={{ width: `${(c.count / maxCat) * 100}%` }}
                          />
                        </div>
                        <span className="w-12 shrink-0 text-right text-xs tabular-nums text-zinc-400">
                          {c.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tag principali */}
              {tags.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    <TagIcon size={11} /> Tag principali (top {tags.length})
                  </div>
                  <ul className="space-y-1.5">
                    {tags.map((t) => (
                      <TagKpiRow key={t.id} tag={t} max={maxTag} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function TagKpiRow({ tag, max }: { tag: TagKpiTag; max: number }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex w-44 shrink-0 items-center gap-2">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: tag.color }}
        />
        <span className="truncate text-xs text-zinc-300" title={tag.name}>
          {tag.name}
        </span>
      </span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-800/80">
        <div
          className="h-full rounded-full"
          style={{ width: `${(tag.count / max) * 100}%`, backgroundColor: tag.color }}
        />
      </div>
      <span className="w-12 shrink-0 text-right text-xs tabular-nums text-zinc-400">
        {tag.count}
      </span>
    </li>
  );
}
